import { useState, useEffect } from 'react';
import { X, Download, Smartphone, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';

const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const [isAdminPage, setIsAdminPage] = useState(false);

  useEffect(() => {
    // Check if on admin page
    setIsAdminPage(window.location.pathname.startsWith('/admin'));

    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      return;
    }

    // Check if dismissed recently (don't show for 7 days)
    const dismissKey = isAdminPage ? 'pwa-admin-prompt-dismissed' : 'pwa-prompt-dismissed';
    const dismissed = localStorage.getItem(dismissKey);
    if (dismissed) {
      const dismissedDate = new Date(dismissed);
      const daysSinceDismissed = (Date.now() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24);
      if (daysSinceDismissed < 7) {
        return;
      }
    }

    // Detect iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(isIOSDevice);

    // Listen for install prompt on Android/Desktop
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Show prompt after a delay
      setTimeout(() => setShowPrompt(true), 3000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Show iOS instructions after delay if on iOS and not installed
    if (isIOSDevice) {
      const isInStandaloneMode = window.navigator.standalone === true;
      if (!isInStandaloneMode) {
        setTimeout(() => setShowPrompt(true), 5000);
      }
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, [isAdminPage]);

  const handleInstall = async () => {
    if (isIOS) {
      setShowIOSInstructions(true);
      return;
    }

    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      console.log('PWA installed');
    }
    
    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    setShowIOSInstructions(false);
    const dismissKey = isAdminPage ? 'pwa-admin-prompt-dismissed' : 'pwa-prompt-dismissed';
    localStorage.setItem(dismissKey, new Date().toISOString());
  };

  if (!showPrompt) return null;

  const appName = isAdminPage ? 'Whitewick Admin' : 'Whitewick IT';
  const appDescription = isAdminPage 
    ? 'Manage tickets, customers & assets on the go'
    : 'Quick access to tickets, invoices & support';

  // iOS Instructions Modal
  if (showIOSInstructions) {
    return (
      <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-4">
        <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-md shadow-2xl animate-in slide-in-from-bottom duration-300">
          <div className="p-6">
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 ${isAdminPage ? 'bg-amber-500' : 'bg-[#1e3a5f]'} rounded-xl flex items-center justify-center`}>
                  {isAdminPage ? (
                    <Shield className="w-6 h-6 text-white" />
                  ) : (
                    <Smartphone className="w-6 h-6 text-white" />
                  )}
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Install {appName}</h3>
                  <p className="text-sm text-slate-500">Add to Home Screen</p>
                </div>
              </div>
              <button onClick={handleDismiss} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4 text-sm text-slate-600">
              <div className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-bold">1</span>
                <p>Tap the <strong>Share</strong> button at the bottom of Safari</p>
              </div>
              <div className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-bold">2</span>
                <p>Scroll down and tap <strong>"Add to Home Screen"</strong></p>
              </div>
              <div className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-bold">3</span>
                <p>Tap <strong>"Add"</strong> in the top right corner</p>
              </div>
            </div>

            <Button onClick={handleDismiss} className={`w-full mt-6 text-white ${isAdminPage ? 'bg-amber-500 hover:bg-amber-600' : 'bg-[#1e3a5f] hover:bg-[#2d4a6f]'}`}>
              Got it
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Install Prompt Banner
  return (
    <div className="fixed bottom-4 left-4 right-4 z-50 sm:left-auto sm:right-4 sm:max-w-sm animate-in slide-in-from-bottom duration-300">
      <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden">
        <div className="p-4">
          <div className="flex items-start gap-3">
            <div className={`flex-shrink-0 w-12 h-12 ${isAdminPage ? 'bg-amber-500' : 'bg-[#1e3a5f]'} rounded-xl flex items-center justify-center`}>
              {isAdminPage ? (
                <Shield className="w-6 h-6 text-white" />
              ) : (
                <img src="/icons/icon-96x96.png" alt="Whitewick IT" className="w-8 h-8 rounded" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-slate-900 text-sm">Install {appName}</h3>
              <p className="text-xs text-slate-500 mt-0.5">{appDescription}</p>
            </div>
            <button onClick={handleDismiss} className="text-slate-400 hover:text-slate-600 -mt-1 -mr-1 p-1">
              <X className="w-4 h-4" />
            </button>
          </div>
          
          <div className="flex gap-2 mt-3">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleDismiss}
              className="flex-1 text-xs"
            >
              Not now
            </Button>
            <Button 
              size="sm" 
              onClick={handleInstall}
              className={`flex-1 text-xs text-white ${isAdminPage ? 'bg-amber-500 hover:bg-amber-600' : 'bg-[#1e3a5f] hover:bg-[#2d4a6f]'}`}
            >
              <Download className="w-3 h-3 mr-1" />
              Install
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PWAInstallPrompt;
